import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import s from './Popover.module.css';
import { cx } from '@/lib/cx';

export interface PopoverProps {
  /** Renders the trigger; receives the props it must spread onto its focusable element. */
  trigger: (props: {
    ref: (el: HTMLElement | null) => void;
    onClick: () => void;
    'aria-expanded': boolean;
    'aria-haspopup': 'dialog';
  }) => ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: 'start' | 'end';
  width?: number;
  className?: string;
}

/**
 * A click-to-open floating surface anchored to its trigger, closed by Esc, an outside click or
 * `close()`. Portaled so panels with `overflow: hidden` never clip it.
 */
export function Popover({ trigger, children, align = 'start', width, className }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const surface = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const w = surface.current?.offsetWidth ?? width ?? 240;
      const left = align === 'end' ? r.right - w : r.left;
      setPos({
        top: r.bottom + 6,
        left: Math.max(8, Math.min(left, window.innerWidth - w - 8)),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchor, align, width]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (surface.current?.contains(t) || anchor?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open, anchor]);

  return (
    <>
      {trigger({
        ref: setAnchor,
        onClick: () => setOpen((o) => !o),
        'aria-expanded': open,
        'aria-haspopup': 'dialog',
      })}
      {open &&
        createPortal(
          <div
            ref={surface}
            role="dialog"
            className={cx(s.surface, className)}
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width }}
          >
            {typeof children === 'function' ? children(close) : children}
          </div>,
          document.body,
        )}
    </>
  );
}
